import type { DetectionRegion, Rect } from '../types'
import { createId } from '../core/id'
import { expandRect } from '../core/rect'

let prepared = false

function assetUrl(path: string): string {
  return new URL(path, document.baseURI).href
}

export async function detectBarcodes(
  canvas: HTMLCanvasElement,
  originalWidth: number,
  originalHeight: number,
  analysisScale: number,
): Promise<DetectionRegion[]> {
  const zxing = await import('zxing-wasm/reader')
  if (!prepared) {
    zxing.prepareZXingModule({
      overrides: {
        locateFile: (path: string, prefix: string) => path.endsWith('.wasm') ? assetUrl('./assets/zxing/zxing_reader.wasm') : `${prefix}${path}`,
      },
    })
    prepared = true
  }

  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) throw new Error('コード検出用の画像データを読み出せません')
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
  const results = await zxing.readBarcodes(imageData, {
    tryHarder: true,
    tryRotate: true,
    tryInvert: true,
    maxNumberOfSymbols: 32,
  })

  return results.map((result) => {
    const points = Object.values(result.position)
    const left = Math.min(...points.map((point) => point.x)) / analysisScale
    const top = Math.min(...points.map((point) => point.y)) / analysisScale
    const right = Math.max(...points.map((point) => point.x)) / analysisScale
    const bottom = Math.max(...points.map((point) => point.y)) / analysisScale
    const rect: Rect = { x: left, y: top, width: right - left, height: bottom - top }
    const isQr = result.format === 'QRCode'
    return {
      id: createId(isQr ? 'qr' : 'barcode'),
      rect: expandRect(rect, Math.max(6, rect.width * 0.06), originalWidth, originalHeight),
      category: isQr ? 'qr' as const : 'barcode' as const,
      source: 'barcode' as const,
      label: isQr ? 'QRコード' : `${result.format}バーコード`,
      confidence: 0.99,
      enabled: true,
      evidence: result.text ? result.text.slice(0, 120) : undefined,
    }
  })
}
